import i18n from '@/i18n';
import { getMerchantTz } from './merchant-tz';

/**
 * Frontend datetime RENDERING, in the merchant timezone and the operator's
 * language.
 *
 * The zone itself and the arithmetic over it live in `merchant-tz.ts`, which
 * carries no i18n dependency so the query serializer can convert a filter
 * bound without pulling the i18n runtime in. Everything it exports is
 * re-exported at the bottom of this file, so `@lib/datetime` remains the one
 * import path callers need.
 *
 * Datetimes on the wire are ISO 8601 with `Z` suffix (UTC). The
 * functions below take any of:
 *   - ISO string (`2026-04-28T14:30:00.000Z`)
 *   - Date instance
 *   - epoch milliseconds
 * and render them in the merchant timezone using the user's i18n
 * locale (so number formatting / month names / am-pm follow the UI
 * language while the wall-clock day/hour stays anchored to the
 * merchant).
 */

/**
 * Suppress duplicate `formatIsoWithOffset` engine-misbehavior
 * warnings. Bounded by the cardinality of distinct merchant
 * timezones the operator has hit (~1 in practice); without this a
 * 1000-row table render produces 1000 identical `console.warn`s.
 */
const offsetWarnLogged = new Set<string>();

function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * A `YYYY-MM-DD` wire value is a calendar day with no timezone
 * semantics (jobs shipping_date, orders delivery_date, products
 * shipping_date). Converting it through the merchant timezone would
 * shift the displayed day (#1160): `new Date('2026-07-13')` is UTC
 * midnight, which is still Jul 12 in PT.
 */
function isDateOnly(value: unknown): value is string {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

/**
 * Calendar date in the merchant timezone, formatted for the active
 * UI locale. Default style is `medium` (e.g. `Apr 28, 2026`).
 */
export function formatDate(
  value: string | number | Date | null | undefined,
  options?: { style?: 'medium' | 'short' | 'long' | 'full' },
): string {
  const date = toDate(value);
  if (!date) return '-';
  // Date-only input: format at UTC so the calendar day passes through
  // unchanged instead of being re-anchored to the merchant timezone.
  const timeZone = isDateOnly(value) ? 'UTC' : getMerchantTz();
  return new Intl.DateTimeFormat(i18n.language, {
    timeZone,
    dateStyle: options?.style ?? 'medium',
  }).format(date);
}

/**
 * Calendar date + time-of-day in the merchant timezone. Default style
 * is `medium` date + `short` time + a trailing tz abbreviation so
 * operators can always tell which timezone they're looking at.
 */
export function formatDateTime(
  value: string | number | Date | null | undefined,
  options?: {
    dateStyle?: 'medium' | 'short' | 'long' | 'full';
    timeStyle?: 'short' | 'medium' | 'long';
    showTimezone?: boolean;
  },
): string {
  const date = toDate(value);
  if (!date) return '-';
  const tz = getMerchantTz();
  const showTz = options?.showTimezone ?? true;
  const formatted = new Intl.DateTimeFormat(i18n.language, {
    timeZone: tz,
    dateStyle: options?.dateStyle ?? 'medium',
    timeStyle: options?.timeStyle ?? 'short',
  }).format(date);
  if (!showTz) return formatted;
  // `timeZoneName: 'short'` produces e.g. "PDT" / "PST" via Intl.
  // We append it ourselves rather than letting Intl interleave it,
  // because the placement varies by locale and we want a stable
  // `<datetime> <tz>` shape across `i18n.language` values.
  const parts = new Intl.DateTimeFormat(i18n.language, {
    timeZone: tz,
    timeZoneName: 'short',
  }).formatToParts(date);
  const tzPart = parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  return tzPart ? `${formatted} ${tzPart}` : formatted;
}

/**
 * The top-bar clock (#2476 T3), e.g. `周四 10月1日 · 09:40 PDT` /
 * `Thu October 1 · 09:40 PDT`.
 *
 * Weekday, date and time each come from `Intl` in `language`, in `timeZone`
 * (the merchant's), and are joined by the `shell.clock` template. The zone is
 * the backend's `abbreviation` (`/api/public/system/timezone`): `Intl` in a
 * non-English language names the zone `GMT-7`, and asking `Intl` for English
 * would hard-code a locale. When the abbreviation is missing, `Intl`'s own
 * zone name in `language` stands in, as in `formatDateTime`.
 */
export function formatShellClock(
  now: Date,
  timeZone: string,
  abbreviation: string,
  language: string,
  t: (key: string, options: Record<string, string>) => string,
): string {
  const part = (options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(language, { timeZone, ...options }).format(now);
  const zone =
    abbreviation ||
    (new Intl.DateTimeFormat(language, { timeZone, timeZoneName: 'short' })
      .formatToParts(now)
      .find((p) => p.type === 'timeZoneName')?.value ??
      '');
  return t('shell.clock', {
    weekday: part({ weekday: 'short' }),
    date: part({ month: 'long', day: 'numeric' }),
    time: part({ hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }),
    zone,
  }).trim();
}

/**
 * The phone header's second line (#2487 H2), e.g. `今天 10/1 周四 · 15:20 PDT` /
 * `Today Thu 10/1 · 15:20 PDT`. Same parts and zone rule as `formatShellClock`,
 * with a numeric month/day and the `shell.phoneToday` template.
 */
export function formatPhoneToday(
  now: Date,
  timeZone: string,
  abbreviation: string,
  language: string,
  t: (key: string, options: Record<string, string>) => string,
): string {
  const part = (options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(language, { timeZone, ...options }).format(now);
  const zone =
    abbreviation ||
    (new Intl.DateTimeFormat(language, { timeZone, timeZoneName: 'short' })
      .formatToParts(now)
      .find((p) => p.type === 'timeZoneName')?.value ??
      '');
  return t('shell.phoneToday', {
    md: part({ month: 'numeric', day: 'numeric' }),
    weekday: part({ weekday: 'short' }),
    time: part({ hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }),
    zone,
  }).trim();
}

/**
 * Time-of-day only in the merchant timezone, e.g. `2:30 PM`.
 */
export function formatTime(
  value: string | number | Date | null | undefined,
  options?: { style?: 'short' | 'medium' | 'long' },
): string {
  const date = toDate(value);
  if (!date) return '-';
  return new Intl.DateTimeFormat(i18n.language, {
    timeZone: getMerchantTz(),
    timeStyle: options?.style ?? 'short',
  }).format(date);
}

/**
 * ISO 8601 with the merchant timezone offset, e.g.
 * `2026-04-28T14:30:00-07:00`. Useful for tooltips and copy-paste
 * surfaces where the operator wants a self-describing value.
 *
 * Implementation note: hand-assembled rather than using `date-fns-tz`
 * because the frontend does not currently depend on `date-fns` and
 * the ~80KB bundle hit is not worth it for this single call site.
 * `Intl.DateTimeFormat` with the `sv-SE` locale produces an ISO-8601-
 * shaped date+time, and `shortOffset` gives the `GMT±H[:MM]` form we
 * normalize to the canonical `±HH:MM` / `Z` suffix.
 */
export function formatIsoWithOffset(value: string | number | Date | null | undefined): string {
  const date = toDate(value);
  if (!date) return '-';
  const tz = getMerchantTz();
  // `sv-SE` formats as `YYYY-MM-DD HH:MM:SS` (24h, ISO-shaped).
  // Replace the space with `T` to satisfy RFC 3339.
  const isoLocal = new Intl.DateTimeFormat('sv-SE', {
    timeZone: tz,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
    .format(date)
    .replace(' ', 'T')
    // sv-SE / hour12:false renders midnight as `24:MM:SS` in some
    // engines; normalize to `00:`.
    .replace(/T24:/, 'T00:');
  // `shortOffset` → `GMT-7` / `GMT+05:30` / `GMT+0` (UTC). The bare
  // `GMT` form Intl never actually emits for UTC zones — engines
  // produce `GMT+0` instead — so we normalize a sign-prefix offset of
  // exactly zero back to the canonical `Z` suffix.
  //
  // If `formatToParts` ever returns no `timeZoneName` part (engine bug
  // or unexpected output), `rawPart` is `undefined`. We treat that as
  // an explicit error signal — log a warning and emit `Z`. Silently
  // defaulting to `'GMT'` was load-bearing magic that hid genuine
  // engine misbehavior behind a "looks UTC" output.
  const rawPart = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' })
    .formatToParts(date)
    .find((p) => p.type === 'timeZoneName')?.value;
  if (rawPart === undefined) {
    if (!offsetWarnLogged.has(tz)) {
      console.warn(
        `[datetime] Intl.formatToParts returned no timeZoneName for tz=${tz}; falling back to Z.`,
      );
      offsetWarnLogged.add(tz);
    }
    return `${isoLocal}Z`;
  }
  const raw = rawPart;
  const m = raw.match(/^GMT(?:([+-])(\d{1,2})(?::(\d{2}))?)?$/);
  let offset: string;
  if (!m || !m[1]) {
    offset = 'Z';
  } else {
    const hh = m[2].padStart(2, '0');
    const mm = m[3] ?? '00';
    offset = hh === '00' && mm === '00' ? 'Z' : `${m[1]}${hh}:${mm}`;
  }
  return `${isoLocal}${offset}`;
}

/**
 * Coarse human relative formatter — `Today`, `1 day`, `30 days`,
 * `2 months`. Replaces the legacy `formatDatetimeSince` in
 * `lib/utils.ts`. Locale-aware via `Intl.RelativeTimeFormat`.
 */
export function formatRelativeFromNow(value: string | number | Date | null | undefined): string {
  const date = toDate(value);
  if (!date) return '-';
  const diffMs = Date.now() - date.getTime();
  const day = 24 * 60 * 60 * 1000;
  const diffDays = Math.floor(Math.abs(diffMs) / day);
  if (diffDays === 0) return i18n.t('ui.today', { defaultValue: 'Today' });
  const rtf = new Intl.RelativeTimeFormat(i18n.language, { numeric: 'auto' });
  if (diffDays < 30) return rtf.format(-Math.sign(diffMs) * diffDays, 'day');
  if (diffDays < 365) return rtf.format(-Math.sign(diffMs) * Math.floor(diffDays / 30), 'month');
  return rtf.format(-Math.sign(diffMs) * Math.floor(diffDays / 365), 'year');
}

/** A relative ETA rendered for the dispatch route card (#1463). */
export interface RelativeEta {
  /** Localized human text, e.g. `~25 min`, `~1h 30m`, `12 min late`. */
  text: string;
  /** True when the ETA is already in the past (stop is running late). */
  overdue: boolean;
}

/**
 * Minute-granularity relative time for a live route ETA (#1463).
 *
 * Unlike `formatRelativeFromNow` (day granularity, for created/updated
 * timestamps), this reads out the minutes/hours until a driver reaches a
 * stop, and flags a past ETA as overdue so the caller can weaken its
 * styling. Buckets:
 *   - `< 1 min` in the future → "arriving now"
 *   - `< 60 min`              → whole minutes
 *   - `>= 60 min`             → hours (+ minutes when non-zero)
 *   - past                    → overdue with elapsed minutes
 *
 * `now` is injectable so the buckets are deterministic under test; the
 * 60s route refetch re-renders it, so no per-second timer is needed.
 */
export function formatRelativeEta(
  value: string | number | Date | null | undefined,
  now: Date = new Date(),
): RelativeEta | null {
  const date = toDate(value);
  if (!date) return null;
  const diffMs = date.getTime() - now.getTime();
  const minute = 60 * 1000;
  if (diffMs < 0) {
    const minutes = Math.max(1, Math.round(-diffMs / minute));
    return { text: i18n.t('ui.relativeEta.overdue', { minutes }), overdue: true };
  }
  if (diffMs < minute) {
    return { text: i18n.t('ui.relativeEta.imminent'), overdue: false };
  }
  const totalMinutes = Math.floor(diffMs / minute);
  if (totalMinutes < 60) {
    return { text: i18n.t('ui.relativeEta.minutes', { minutes: totalMinutes }), overdue: false };
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const text =
    minutes === 0
      ? i18n.t('ui.relativeEta.hours', { hours })
      : i18n.t('ui.relativeEta.hoursMinutes', { hours, minutes });
  return { text, overdue: false };
}

/**
 * The timezone kernel, re-exported so `@lib/datetime` stays the single import
 * path for everything timezone-related. `merchant-tz.ts` exists to give the
 * i18n-free half its own module, not to make callers pick between two.
 */
export {
  DEFAULT_MERCHANT_TZ,
  getMerchantTz,
  merchantDayStartUtc,
  merchantWallTimeToUtcIso,
  setMerchantTz,
  todayInMerchantTz,
} from './merchant-tz';
